import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { computed, DestroyRef, inject, Signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TableSettingsKey } from '@core/enums/table-settings-key.enum';
import { PackagePriceHistoryResponse } from '@core/interfaces/price-history.model';
import { ResponseModel } from '@core/interfaces/response.model';
import { ApiDrugsService } from '@core/services/api/api-drugs.service';
import { CalcCssVarsService } from '@core/services/calc-css-vars.service';
import { ToastService } from '@core/services/toast.service';
import { tapResponse } from '@ngrx/operators';
import { getState, patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { TranslateService } from '@ngx-translate/core';
import { DrugsItemDetailInterface, DrugsItemInterface } from '@store/drugs/drugs-item.inteface';
import { TableSettingsStore } from '@store/table-settings/table-settings.store';
import { catchError, filter, first, of, switchMap, throwError } from 'rxjs';
import { DictionaryModel } from './../../core/interfaces/dictionary.model';

export interface DrugsItemState {
    tableData?: ResponseModel<DrugsItemInterface> | null;
    isFirstLoad: boolean;
    hasMore: boolean;
}

export interface DrugsState {
    drugs: {
        [key: string]: DrugsItemState;
    } | null;
    selectedDrug: DrugsItemDetailInterface | null;
    selectedDrugDynamicPrice: PackagePriceHistoryResponse | null;
    isLoading: boolean;
    isLoadingDrug: boolean;
    isLoadingDrugDynamicPrice: boolean;
    isLoadingExport: boolean;
    error: string | null;
    errorDynamicPrice: string | null;
    releaseForm: DictionaryModel<string, string>[] | null;
}

const initialState: DrugsState = {
    drugs: null,
    selectedDrug: null,
    selectedDrugDynamicPrice: null,
    isLoadingDrug: false,
    isLoading: false,
    isLoadingDrugDynamicPrice: false,
    isLoadingExport: false,
    error: null,
    errorDynamicPrice: null,
    releaseForm: null,
};

export const DrugsStore = signalStore(
    { providedIn: 'root' },
    withState(initialState),
    withMethods(
        (
            store,
            apiDrugsService = inject(ApiDrugsService),
            tableSettings = inject(TableSettingsStore),
            destroy = inject(DestroyRef),
            router = inject(Router),
            translateService = inject(TranslateService),
            toastService = inject(ToastService),
            calcCssVarsService = inject(CalcCssVarsService),
        ) => {
            return {
                loadDrugs(key: TableSettingsKey): void {
                    patchState(store, (state) => ({
                        isLoading: true,
                        drugs: {
                            ...(state.drugs || {}),
                            [key]: {
                                isFirstLoad: false,
                                hasMore: store.drugs()?.[key]?.hasMore ?? false,
                                ...(state.drugs && state.drugs[key] ? state.drugs[key] : {}),
                            },
                        },
                        error: null,
                    }));
                    const params = untracked(() => tableSettings.getTableRequestSettingsForSection(key)());

                    const settings = untracked(() => tableSettings.getTableSettingsForSection(key)());
                    const isLoadMore = settings?.isLoadMore;
                    if (!params) {
                        patchState(store, {
                            isLoading: false,
                            error: null,
                        });

                        return;
                    }
                    apiDrugsService
                        .getDrugsList(params)
                        .pipe(
                            first(),
                            tapResponse({
                                next: (data) => {
                                    patchState(store, (state) => {
                                        const previousTableData = state.drugs?.[key]?.tableData;
                                        const previousData = previousTableData?.data ?? [];
                                        const isAppend = isLoadMore && previousData.length > 0;
                                        const mergedData = isAppend ? [...previousData, ...data.data] : data.data;

                                        const mergedLength = mergedData.length;
                                        const hasMore = mergedData.length < (data.totalCount ?? 0);

                                        return {
                                            ...state,
                                            isLoading: false,
                                            drugs: {
                                                ...(state.drugs || {}),
                                                [key]: {
                                                    tableData: {
                                                        totalCount: data.totalCount,
                                                        data: mergedData,
                                                    },
                                                    isFirstLoad: true,
                                                    hasMore,
                                                },
                                            },
                                        };
                                    });
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, {
                                        isLoading: false,
                                    });
                                },
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe();
                },
                loadDrugsAnalogues(key: TableSettingsKey): void {
                    patchState(store, (state) => ({
                        isLoading: true,
                        drugs: {
                            ...(state.drugs || {}),
                            [key]: {
                                isFirstLoad: false,
                                hasMore: store.drugs()?.[key]?.hasMore ?? false,
                                ...(state.drugs && state.drugs[key] ? state.drugs[key] : {}),
                            },
                        },
                        error: null,
                    }));
                    const params = untracked(() => tableSettings.getTableRequestSettingsForSection(key)());
                    const settings = untracked(() => tableSettings.getTableSettingsForSection(key)());
                    const isLoadMore = settings?.isLoadMore;
                    if (!params) {
                        patchState(store, {
                            isLoading: false,
                            error: null,
                        });

                        return;
                    }
                    apiDrugsService
                        .getDrugsAnaloguesList(params)
                        .pipe(
                            first(),
                            tapResponse({
                                next: (data) => {
                                    patchState(store, (state) => {
                                        const previousTableData = state.drugs?.[key]?.tableData;
                                        const previousData = previousTableData?.data ?? [];
                                        const isAppend = isLoadMore && previousData.length > 0;
                                        const mergedData = isAppend ? [...previousData, ...data.data] : data.data;

                                        const mergedLength = mergedData.length;
                                        const hasMore = mergedData.length < (data.totalCount ?? 0);

                                        return {
                                            ...state,
                                            isLoading: false,
                                            drugs: {
                                                ...(state.drugs || {}),
                                                [key]: {
                                                    tableData: {
                                                        totalCount: data.totalCount,
                                                        data: mergedData,
                                                    },
                                                    isFirstLoad: true,
                                                    hasMore,
                                                },
                                            },
                                        };
                                    });
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, {
                                        isLoading: false,
                                    });
                                },
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe();
                },
                loadDrugsByMask(key: TableSettingsKey): void {
                    patchState(store, (state) => ({
                        isLoading: true,
                        drugs: {
                            ...(state.drugs || {}),
                            [key]: {
                                isFirstLoad: false,
                                hasMore: store.drugs()?.[key]?.hasMore ?? false,
                                ...(state.drugs && state.drugs[key] ? state.drugs[key] : {}),
                            },
                        },
                        error: null,
                    }));
                    const params = untracked(() => tableSettings.getTableRequestSettingsForSection(key)());
                    const settings = untracked(() => tableSettings.getTableSettingsForSection(key)());
                    const isLoadMore = settings?.isLoadMore;
                    if (!params) {
                        patchState(store, {
                            isLoading: false,
                            error: null,
                        });

                        return;
                    }
                    apiDrugsService
                        .getDrugsByMaskList(params)
                        .pipe(
                            first(),
                            tapResponse({
                                next: (data) => {
                                    patchState(store, (state) => {
                                        const previousTableData = state.drugs?.[key]?.tableData;
                                        const previousData = previousTableData?.data ?? [];
                                        const isAppend = isLoadMore && previousData.length > 0;
                                        const mergedData = isAppend ? [...previousData, ...data.data] : data.data;

                                        const hasMore = mergedData.length < (data.totalCount ?? 0);

                                        return {
                                            ...state,
                                            isLoading: false,
                                            drugs: {
                                                ...(state.drugs || {}),
                                                [key]: {
                                                    tableData: {
                                                        totalCount: data.totalCount,
                                                        data: mergedData,
                                                    },
                                                    isFirstLoad: true,
                                                    hasMore,
                                                },
                                            },
                                        };
                                    });
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, {
                                        isLoading: false,
                                    });
                                },
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe();
                },

                loadDrugsAnaloguesWithPrice(key: TableSettingsKey): void {
                    patchState(store, (state) => ({
                        isLoading: true,
                        drugs: {
                            ...(state.drugs || {}),
                            [key]: {
                                isFirstLoad: false,
                                hasMore: store.drugs()?.[key]?.hasMore ?? false,
                                ...(state.drugs && state.drugs[key] ? state.drugs[key] : {}),
                            },
                        },
                        error: null,
                    }));
                    const params = untracked(() => tableSettings.getTableRequestSettingsForSection(key)());

                    // tradeName — легальний необов'язковий параметр пошуку за номерами РП, тому перевірка на РП — першою;
                    // масив номерів РП відрізняємо від одиночного rcNumber вкладки "За торговельною назвою" за типом значення
                    const isRcNumber = params?.filters?.some((filter) => filter.key === 'rcNumber' && Array.isArray(filter.value));
                    const isTradeName = params?.filters?.find((filter) => filter.key === 'tradeName');
                    const settings = untracked(() => tableSettings.getTableSettingsForSection(key)());
                    const isLoadMore = settings?.isLoadMore;

                    if (!params) {
                        patchState(store, {
                            isLoading: false,
                            error: null,
                        });

                        return;
                    }

                    (isRcNumber
                        ? apiDrugsService.getDrugsAnaloguesListWithPriceByRegistrationCertificate(params)
                        : isTradeName
                          ? apiDrugsService.getDrugsAnaloguesListWithPriceByTradeName(params)
                          : apiDrugsService.getDrugsAnaloguesListWithPrice(params)
                    )
                        .pipe(
                            first(),
                            tapResponse({
                                next: (data) => {
                                    patchState(store, (state) => {
                                        const previousTableData = state.drugs?.[key]?.tableData;
                                        const previousData = previousTableData?.data ?? [];
                                        const isAppend = isLoadMore && previousData.length > 0;
                                        const mergedData = isAppend ? [...previousData, ...data.data] : data.data;

                                        const mergedLength = mergedData.length;
                                        const hasMore = mergedData.length < (data.totalCount ?? 0);

                                        return {
                                            ...state,
                                            isLoading: false,
                                            drugs: {
                                                ...(state.drugs || {}),
                                                [key]: {
                                                    tableData: {
                                                        totalCount: data.totalCount,
                                                        data: mergedData,
                                                    },
                                                    isFirstLoad: true,
                                                    hasMore,
                                                },
                                            },
                                        };
                                    });
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, {
                                        isLoading: false,
                                    });
                                },
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe();
                },

                loadReleaseForm(): void {
                    apiDrugsService
                        .getReleaseForm()
                        .pipe(
                            first(),
                            tapResponse({
                                next: (data) => {
                                    patchState(store, { releaseForm: data });
                                },
                                error: (error: HttpErrorResponse) => {
                                    patchState(store, {
                                        isLoading: false,
                                    });
                                },
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe();
                },

                clearError(): void {
                    patchState(store, { error: null });
                },

                clearDrugs(): void {
                    patchState(store, { drugs: null });
                },
                getDrugsTableData(sectionId: TableSettingsKey): Signal<ResponseModel<DrugsItemInterface> | null> {
                    // @ts-ignore
                    return computed(() => (getState(store)?.drugs ? getState(store)?.drugs[sectionId]?.tableData : null));
                },
                getDrugs(sectionId: TableSettingsKey): Signal<DrugsItemState | null> {
                    // @ts-ignore
                    return computed(() => (getState(store)?.drugs ? getState(store)?.drugs[sectionId] : null));
                },
                getDrugById(id: string): void {
                    patchState(store, { selectedDrug: null, isLoadingDrug: true });
                    apiDrugsService
                        .getDrugById(id)
                        .pipe(
                            first(),
                            catchError((err) => {
                                patchState(store, { isLoadingDrug: false });
                                console.log('CATCH MEDICATION err', err);
                                if (err?.error?.Code === 'ErrorGetAnaloguesForPackage') {
                                    router.navigate(['/404']).then();
                                }

                                return throwError(() => err);
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe((drug) => {
                            if (!drug) {
                                router.navigate(['/404']).then();
                            }
                            patchState(store, { selectedDrug: drug, isLoadingDrug: false });
                        });
                },

                loadSelectedDrugDynamicPrice(pcid: string): void {
                    if (!pcid) {
                        patchState(store, { selectedDrugDynamicPrice: null, isLoadingDrugDynamicPrice: false, errorDynamicPrice: null });
                        return;
                    }

                    patchState(store, { selectedDrugDynamicPrice: null, isLoadingDrugDynamicPrice: true, errorDynamicPrice: null });

                    apiDrugsService
                        .getPackagePriceHistory(pcid)
                        .pipe(
                            first(),
                            tapResponse({
                                next: (resp) => {
                                    patchState(store, (state) => ({
                                        selectedDrugDynamicPrice: resp,
                                        isLoadingDrugDynamicPrice: false,
                                        errorDynamicPrice: null,
                                    }));
                                },
                                error: () => {
                                    patchState(store, { isLoadingDrugDynamicPrice: false });
                                },
                            }),
                            takeUntilDestroyed(destroy),
                        )
                        .subscribe();
                },
                exportCatalog(): void {
                    if (store.isLoadingExport()) {
                        return;
                    }

                    patchState(store, { isLoadingExport: true });
                    apiDrugsService
                        .exportCatalog()
                        .pipe(
                            first(),
                            filter((resp): resp is HttpResponse<Blob> => !!resp && !!resp.body),
                            switchMap((resp) => {
                                patchState(store, { isLoadingExport: false });
                                const filename = apiDrugsService.getFilename(resp.headers);
                                apiDrugsService.downloadFile(resp.body!, filename);

                                if (calcCssVarsService.isNotDesktopView$.value) {
                                    return toastService.openToast(translateService.instant('common.download-started'), 'success');
                                }

                                return of(resp);
                            }),
                            takeUntilDestroyed(destroy),
                            catchError((err) => {
                                patchState(store, { isLoadingExport: false });
                                if (calcCssVarsService.isNotDesktopView$.value) {
                                    return toastService.openToast(translateService.instant('common.error'), 'danger');
                                }

                                return throwError(() => err);
                            }),
                        )
                        .subscribe();
                },
            };
        },
    ),
);
